"use client";

import { c as _c } from "react/compiler-runtime";
import type { MagnusUIMessage } from "@bg-bgi/magnus-sdk";
import type { UseMagnusResult } from "@bg-bgi/magnus-sdk/react";
import { Loader } from "@workspace/ui/components/ai-elements/loader";
import { Button } from "@workspace/ui/components/button";
import { ArrowDownIcon, MapPin } from "lucide-react";
import { type AnchorHTMLAttributes, type ComponentType, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ChatFeedbackSource, FeedbackModal } from "./feedback";
import { type MagnusChatMessagePart, type MagnusChatProcessingSnapshot, Message } from "./message";
import type { MessageFeedbackProps } from "./message-actions";
import { MessageErrorBoundary } from "./message-error-boundary";
import { isErroredAssistantMessage, MessageErrorIndicator } from "./message-error-indicator";
const AT_BOTTOM_THRESHOLD_PX = 48;

/** An in-app link a host may route (e.g. Next `<Link>`); defaults to `<a>`. */
export type MagnusChatLinkComponent = ComponentType<AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
}>;
export type MessageAskedFrom = NonNullable<NonNullable<MagnusUIMessage["metadata"]>["askedFrom"]>;

// Metadata reaches the transcript from persisted rows, so the url is checked
// again here rather than trusted: only a reader page renders as a link.
const READER_PATH = /^\/p\/[\w-]+$/;
export function getMessageAskedFrom(message: MagnusUIMessage): MessageAskedFrom | undefined {
  return message.metadata?.askedFrom;
}

/**
 * The user turns that start a new run of "asked from" — the first turn sent
 * from a place or page, and each turn sent from a different one after it.
 */
export function askedFromHeaders(messages: MagnusUIMessage[]): Map<string, MessageAskedFrom> {
  const headers = new Map<string, MessageAskedFrom>();
  let previous: string | undefined;
  for (const message of messages) {
    if (message.role !== "user") {
      continue;
    }
    const askedFrom = getMessageAskedFrom(message);
    if (askedFrom && askedFrom.label !== previous) {
      headers.set(message.id, askedFrom);
    }
    previous = askedFrom?.label;
  }
  return headers;
}
function DefaultLink(t0) {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <a {...rest}>{children}</a>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}

/** Where in the host a user turn was sent from: a place, or the page being read. */
export function AskedFromTag(t0) {
  const $ = _c(7);
  const {
    askedFrom,
    linkComponent: t1
  } = t0;
  const Link = t1 === undefined ? DefaultLink : t1;
  const href = askedFrom.url && READER_PATH.test(askedFrom.url) ? askedFrom.url : undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MapPin aria-hidden={true} className="size-3 shrink-0" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== Link || $[2] !== askedFrom.label || $[3] !== href) {
    t3 = href ? <Link className="hover:underline" href={href}>{askedFrom.label}</Link> : askedFrom.label;
    $[1] = Link;
    $[2] = askedFrom.label;
    $[3] = href;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="mb-0.5 flex items-center justify-end gap-1 text-[10px] text-muted-foreground/50">{t2}<span className="max-w-[16rem] truncate">From{" "}{t3}</span></div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

/**
 * Scrollable message list. Follows the stream while the user is at the
 * bottom; shows a jump-to-bottom button once they scroll up. When the
 * host injects the `feedback` source, assistant messages get thumbs
 * up/down that open the feedback modal.
 *
 * Host seams (all optional, absent = today's behavior):
 *  - `banner` renders at the top of the list (e.g. a "preparing files"
 *    notice)
 *  - `renderError` renders the transport error at the bottom of the list
 *  - `renderMessageDebug` mounts beneath each bubble; absent → the
 *    per-message error indicator on failed assistant turns, with "Report
 *    error" routed through the `feedback` source's modal
 *  - `renderMessageHeader` mounts above each row (surface tags/dividers);
 *    the "asked from" tag renders beneath it on its own
 *  - `linkComponent` routes the transcript's in-app links
 *  - `attachmentStatuses` / `onRetryProcessing` / `onAttachmentActivate`
 *    drive live processing dots, retry, and click behavior on sent files
 */
export function Transcript({
  attachmentStatuses,
  banner,
  bottomPad,
  emptyState,
  error,
  feedback,
  linkComponent,
  messages,
  onAttachmentActivate,
  onRetryProcessing,
  renderError,
  renderMessageDebug,
  renderMessageHeader,
  renderMessageMenuLabel,
  renderMessageMeta,
  renderUserMessage,
  scrollButtonOffset,
  smoothTextEnabled = true,
  status
}: {
  /** Live processing snapshots for sent attachments, keyed by upload id. */
  attachmentStatuses?: ReadonlyMap<string, MagnusChatProcessingSnapshot>;
  /** Rendered at the top of the message list (e.g. a preparing-files
   *  notice). */
  banner?: ReactNode;
  /** Extra bottom padding (px) on the message column — used by the docked
   *  composer layout so the dock never covers the last message. */
  bottomPad?: number;
  emptyState?: ReactNode;
  /** The engine's transport error; rendered via `renderError` when set. */
  error?: Error;
  /** Thumbs/modal source. Absent → no feedback affordances. */
  feedback?: ChatFeedbackSource;
  /** Renders in-app links (the "asked from" page). Absent → plain `<a>`. */
  linkComponent?: MagnusChatLinkComponent;
  messages: MagnusUIMessage[];
  /** Replaces the default attachment click-to-open. */
  onAttachmentActivate?: (part: MagnusChatMessagePart) => void;
  /** Retries tier-2 processing on a failed sent attachment. */
  onRetryProcessing?: (uploadId: string) => void;
  /** Transport-error renderer; when set, the composer's static error
   *  banner is suppressed in favor of this transcript-level one. */
  renderError?: (error: Error) => ReactNode;
  /** Per-message debug slot, rendered beneath each bubble. Defaults to
   *  {@link MessageErrorIndicator} on failed assistant turns. */
  renderMessageDebug?: (message: MagnusUIMessage) => ReactNode;
  /** Mounted above each message row (e.g. surface-switch dividers/tags).
   *  Receives the row's message and the previous (deduped) message. */
  renderMessageHeader?: (message: MagnusUIMessage, prevMessage?: MagnusUIMessage) => ReactNode;
  /** Caption at the top of an assistant row's "…" menu (e.g. the model
   *  that produced the turn). */
  renderMessageMenuLabel?: (message: MagnusUIMessage) => ReactNode;
  /** Mounted at the head of the hover action row (e.g. timestamps). */
  renderMessageMeta?: (message: MagnusUIMessage) => ReactNode;
  renderUserMessage?: (message: MagnusUIMessage) => ReactNode;
  /** Bottom offset (px) for the jump-to-bottom button — the docked layout
   *  lifts it above the composer dock. */
  scrollButtonOffset?: number;
  /** False while replaying a buffered stream — text snaps in. */
  smoothTextEnabled?: boolean;
  status: UseMagnusResult["status"];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [feedbackModal, setFeedbackModal] = useState<{
    messageId: string;
    vote: "up" | "down";
  } | null>(null);

  // Last-wins dedup by id: a reconnect replay can emit the same message
  // twice; the later copy is the fresher one (matches the web app).
  const uniqueMessages = useMemo(() => {
    const lastIndex = new Map<string, number>();
    messages.forEach((m, i) => {
      lastIndex.set(m.id, i);
    });
    return messages.filter((m_0, i_0) => lastIndex.get(m_0.id) === i_0);
  }, [messages]);
  const askedFromByMessageId = useMemo(() => askedFromHeaders(uniqueMessages), [uniqueMessages]);
  const inFlight = status === "submitted" || status === "streaming";
  // The newest assistant turn keeps its action row open — it's the reply the
  // user is actually looking at, and making them hunt for Copy by hovering is
  // the flagship transcript's behavior we'd otherwise lose.
  const lastAssistantId = useMemo(() => {
    for (let i_1 = uniqueMessages.length - 1; i_1 >= 0; i_1--) {
      if (uniqueMessages[i_1]?.role === "assistant") {
        return uniqueMessages[i_1]?.id;
      }
    }
    return;
  }, [uniqueMessages]);
  // Owner-only server-side; a host that knows the viewer is a collaborator
  // says so here rather than letting them click into a 403.
  const canVoteInChat = feedback?.canSubmit !== false;

  // Stable per-row feedback props, keyed by message id. `Message` is memoized so
  // a streaming turn only re-renders the row whose message object changed, but a
  // fresh `{ onVote, vote }` literal per row would defeat that for every row on
  // every token. Keyed on the id LIST rather than the array identity: a delta
  // replaces the array and its last message while the ids, recorded votes, and
  // eligibility rule all stay put.
  const messageIdsKey = uniqueMessages.map(m_1 => m_1.id).join("\u0000");
  const messagesRef = useRef(uniqueMessages);
  messagesRef.current = uniqueMessages;
  const feedbackMap = feedback?.map;
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on messageIdsKey, not the per-delta array identity
  const feedbackByMessageId = useMemo(() => {
    const byId = new Map<string, MessageFeedbackProps>();
    if (!feedbackMap) {
      return byId;
    }
    const rows = messagesRef.current;
    rows.forEach((message, index) => {
      // Voting is stricter than copying — an errored turn's assistant row may
      // never have been persisted, and a vote on it 404s. Also hidden for
      // collaborators (feedback is owner-only server-side).
      const canVote = canVoteInChat && (index !== rows.length - 1 || status === "ready");
      if (message.role === "user" || !canVote) {
        return;
      }
      byId.set(message.id, {
        onVote: vote => setFeedbackModal({
          messageId: message.id,
          vote
        }),
        vote: feedbackMap[message.id]?.vote
      });
    });
    return byId;
  }, [canVoteInChat, feedbackMap, messageIdsKey, status]);

  // Default per-message slot (plan 043 P6): the error indicator under a
  // persisted failure. "Report error" is a thumbs-down with a comment, so it
  // opens the same modal through the same `feedback` source — an absent
  // source (or a collaborator who can't vote) leaves the notice read-only. A
  // host `renderMessageDebug` replaces this whole default (the flagship
  // composes the same indicator with its debug panel).
  const canReportErrors = Boolean(feedback) && canVoteInChat;
  const defaultRenderMessageDebug = useCallback((message_0: MagnusUIMessage) => {
    if (!isErroredAssistantMessage(message_0)) {
      return null;
    }
    return <MessageErrorIndicator errorCode={message_0.metadata?.errorCode} errorMessage={message_0.metadata?.errorMessage} hasReported={feedbackMap?.[message_0.id] !== undefined} onReportError={canReportErrors ? () => setFeedbackModal({
      messageId: message_0.id,
      vote: "down"
    }) : undefined} />;
  }, [canReportErrors, feedbackMap]);
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < AT_BOTTOM_THRESHOLD_PX);
  }, []);
  const scrollToBottom = useCallback((behavior: ScrollBehavior) => {
    const el_0 = scrollRef.current;
    el_0?.scrollTo({
      top: el_0.scrollHeight,
      behavior
    });
  }, []);

  // Follow streamed content only while the user hasn't scrolled away.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on every transcript change
  useEffect(() => {
    if (atBottom) {
      scrollToBottom("instant");
    }
  }, [messages, status, atBottom, scrollToBottom]);
  return <div className="relative min-h-0 flex-1">
      {/* Horizontal padding belongs on the inner column, not here: the
          composer's own inset sits inside its `max-w-3xl`, so padding applied
          outside this one would make the message column wider than the
          composer frame instead of sitting inside it. Inset runs well past
          RichComposer's own (`px-2 sm:px-0`): the flagship transcript reads as
          text set within the composer frame's span, so the message column must
          never be the wider of the two. */}
      <div className="h-full overflow-y-auto py-6" onScroll={handleScroll} ref={scrollRef}>
        {uniqueMessages.length === 0 ?
      // Fill the transcript viewport so the empty state truly centers
      // between header and composer (the message column is content-height,
      // which is what left it top-aligned before). `my-auto` centers but
      // still collapses to top-aligned if the panel is too short to fit.
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col px-3 sm:px-4">
            <div className="my-auto w-full">
              {emptyState ?? <p className="text-center text-muted-foreground text-sm">
                  Send a message to get started.
                </p>}
            </div>
          </div> : <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-3 sm:px-4" style={bottomPad ? {
        paddingBottom: bottomPad
      } : undefined}>
            {banner}
            {uniqueMessages.map((message_1, index_0) => {
          const isLast = index_0 === uniqueMessages.length - 1;
          // The action row (copy / markdown) stays hidden on the live
          // message while a turn is actually in flight — the assistant row
          // may not have persisted yet. A failed turn is NOT in flight: the
          // partial response is all the user gets, so Copy and View-markdown
          // stay reachable. Matches the web app.
          const isLive = isLast && inFlight;
          const askedFrom = askedFromByMessageId.get(message_1.id);
          return <MessageErrorBoundary key={message_1.id} messageId={message_1.id}>
                  {renderMessageHeader?.(message_1, uniqueMessages[index_0 - 1])}
                  {askedFrom ? <AskedFromTag askedFrom={askedFrom} linkComponent={linkComponent} /> : null}
                  <Message attachmentStatuses={attachmentStatuses} feedback={feedbackByMessageId.get(message_1.id)} isLastAssistant={message_1.id === lastAssistantId} message={message_1} onAttachmentActivate={onAttachmentActivate} onRetryProcessing={onRetryProcessing} renderMessageDebug={renderMessageDebug ?? defaultRenderMessageDebug} renderMessageMenuLabel={renderMessageMenuLabel} renderMessageMeta={renderMessageMeta} renderUserMessage={renderUserMessage} smoothTextEnabled={smoothTextEnabled} streaming={isLive} />
                </MessageErrorBoundary>;
        })}
            {inFlight ? <Loader className="self-start" size={8} /> : null}
            {/* Not gated on `status === "error"`: an engine can surface an
                error while reporting a non-error status (e.g. a stream that
                closed with no content), and this is the only place those
                would ever appear. */}
            {error && renderError ? renderError(error) : null}
          </div>}
      </div>
      {feedbackModal && feedback && <FeedbackModal messageId={feedbackModal.messageId} onOpenChange={open => !open && setFeedbackModal(null)} onSuccess={() => setFeedbackModal(null)} open={!!feedbackModal} submit={feedback.submit} vote={feedbackModal.vote} />}
      {!atBottom && <Button aria-label="Scroll to bottom" className="-translate-x-1/2 absolute bottom-4 left-1/2 rounded-full" onClick={() => scrollToBottom("smooth")} size="icon" style={scrollButtonOffset ? {
      bottom: scrollButtonOffset
    } : undefined} type="button" variant="outline">
          <ArrowDownIcon className="size-4" />
        </Button>}
    </div>;
}